/**
 * Hook que produz **2 callbacks de autofill** (CNPJ e CEP) para o
 * `FormRenderer`. Traduz o objeto normalizado retornado pelos hooks
 * `useCnpjLookup`/`useCepLookup` (em `features/leads`) nos IDs
 * convencionais do schema do template.
 *
 * **Convenção de IDs**: a heurística tenta múltiplos nomes por
 * "conceito" (ex: `razao_social` OU `legal_name`), refletindo o que
 * o template canônico (migration 0039) usa + sinônimos técnicos
 * comuns. Builder (M1-01d-i) pode introduzir mapping explícito via
 * metadata do field para eliminar a heurística; por ora ela cobre
 * 100% dos templates atuais.
 *
 * **Só sobrescreve campos vazios** — respeita valores já preenchidos
 * pelo lead (equivalente ao `respectExistingValues` do
 * `LeadFormFields` no painel do Superadmin).
 */
import { useCallback } from 'react';

import type { FormField } from '../../schemas.js';

export interface CnpjAutofillData {
  legal_name?: string | undefined;
  trade_name?: string | undefined;
  phone?: string | undefined;
  email?: string | undefined;
  address_cep?: string | undefined;
  address_street?: string | undefined;
  address_number?: string | undefined;
  address_complement?: string | undefined;
  address_district?: string | undefined;
  address_city?: string | undefined;
  address_uf?: string | undefined;
}

export interface CepAutofillData {
  street?: string | undefined;
  district?: string | undefined;
  city?: string | undefined;
  uf?: string | undefined;
}

export interface UseAutofillMappingArgs {
  allFields: Map<string, FormField>;
  setAnswers: (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => void;
  notifyChange?: ((next: Record<string, unknown>) => void) | undefined;
}

export interface AutofillMapping {
  applyCnpjAutofill: (data: CnpjAutofillData) => void;
  applyCepAutofill: (data: CepAutofillData) => void;
}

/** Mapeamentos canônicos ID-alvo → possíveis aliases do schema.
 * Ordem importa: o primeiro ID existente no schema + vazio recebe o
 * valor; os demais são ignorados. */
const CNPJ_MAP: [keyof CnpjAutofillData, string[]][] = [
  ['legal_name', ['razao_social', 'legal_name']],
  ['trade_name', ['nome_fantasia', 'trade_name', 'clinic_name_guess']],
  ['phone', ['telefone_clinica', 'phone']],
  ['email', ['email_contato', 'email']],
  ['address_cep', ['endereco_cep', 'address_cep', 'cep']],
  ['address_street', ['endereco_rua', 'address_street', 'street']],
  ['address_number', ['endereco_numero', 'address_number', 'number']],
  ['address_complement', ['endereco_complemento', 'address_complement', 'complement']],
  ['address_district', ['endereco_bairro', 'address_district', 'district']],
  ['address_city', ['endereco_cidade', 'address_city', 'city']],
  ['address_uf', ['endereco_uf', 'address_uf', 'uf']],
];

const CEP_MAP: [keyof CepAutofillData, string[]][] = [
  ['street', ['endereco_rua', 'address_street', 'street']],
  ['district', ['endereco_bairro', 'address_district', 'district']],
  ['city', ['endereco_cidade', 'address_city', 'city']],
  ['uf', ['endereco_uf', 'address_uf', 'uf']],
];

function applyMapping<T extends CnpjAutofillData | CepAutofillData>(
  data: T,
  mapping: [keyof T, string[]][],
  allFields: Map<string, FormField>,
  current: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...current };
  for (const [key, aliases] of mapping) {
    const value = data[key];
    if (!value) continue;
    for (const id of aliases) {
      if (allFields.has(id) && !next[id]) {
        next[id] = value;
        break;
      }
    }
  }
  return next;
}

export function useAutofillMapping({
  allFields,
  setAnswers,
  notifyChange,
}: UseAutofillMappingArgs): AutofillMapping {
  const applyCnpjAutofill = useCallback(
    (data: CnpjAutofillData) => {
      setAnswers((prev) => {
        const next = applyMapping(data, CNPJ_MAP, allFields, prev);
        notifyChange?.(next);
        return next;
      });
    },
    [allFields, setAnswers, notifyChange],
  );

  const applyCepAutofill = useCallback(
    (data: CepAutofillData) => {
      setAnswers((prev) => {
        const next = applyMapping(data, CEP_MAP, allFields, prev);
        notifyChange?.(next);
        return next;
      });
    },
    [allFields, setAnswers, notifyChange],
  );

  return { applyCnpjAutofill, applyCepAutofill };
}
